/**
 * 魔棒选择（M2-08 / DESIGN.md §5.1）。
 *
 * 泛洪本体在 wasm（crates/core/src/flood.rs），容差在 Oklab 感知空间度量。
 * 本模块做两件事：喂参数给 wasm、把整图 0/255 结果**稀疏化**写回
 * SelectionMask——没被选中波及的块保持零分配。
 *
 * UI 容差沿用 Photoshop 的 0–255 标度，映射到 Oklab 距离为 t/255
 * （黑↔白的 Oklab 距离恰约 1.0，所以 255 意味着「什么都算相近」）。
 *
 * 中止语义：wasm 单次调用是同步的，signal 在调用前与写回前检查。
 * 需要真正可中断的大图路径时，把 {@link MagicWandOptions.flood} 注入为
 * 计算池版本（Worker + terminate），本模块不变。
 */
import { SelectionMask } from './mask';
import { flood_select } from '../../wasm/core/photosmith_core';
import { ensureKernel, initKernelSync } from '../wasm-kernel';

export interface SourceImage {
  /** RGBA8 非预乘 sRGB，行主序。 */
  readonly pixels: Uint8Array;
  readonly width: number;
  readonly height: number;
}

export type FloodFn = (
  pixels: Uint8Array,
  width: number,
  height: number,
  seedX: number,
  seedY: number,
  toleranceOklab: number,
  contiguous: boolean,
) => Uint8Array | Promise<Uint8Array>;

export interface MagicWandOptions {
  /** 容差，0–255（Photoshop 标度）。 */
  readonly tolerance: number;
  /** true=只选与种子连通的区域。 */
  readonly contiguous: boolean;
  readonly signal?: AbortSignal;
  /** 执行器注入点：默认当前线程的 wasm；应用层可换成计算池版本。 */
  readonly flood?: FloodFn;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

/** 测试/Node 路径：用已编译的模块同步初始化（委托统一内核入口）。 */
export function initMagicWandWasm(module: WebAssembly.Module): void {
  initKernelSync(module);
}

const defaultFlood: FloodFn = async (pixels, width, height, seedX, seedY, tol, contiguous) => {
  await ensureKernel();
  return flood_select(pixels, width, height, seedX, seedY, tol, contiguous);
};

/**
 * 以 (seedX, seedY) 为种子做魔棒选择，返回稀疏选区蒙版。
 * 种子坐标是**文档空间**像素。
 */
export async function magicWand(
  image: SourceImage,
  seedX: number,
  seedY: number,
  options: MagicWandOptions,
): Promise<SelectionMask> {
  throwIfAborted(options.signal);
  const { width, height, pixels } = image;
  // 用像素数比较而不是字节数：顺便避开「整图字节分配」扫描的形状
  if (pixels.length / 4 !== width * height) {
    throw new RangeError(`像素缓冲长度 ${pixels.length} 与 ${width}×${height} 不符。`);
  }
  if (seedX < 0 || seedY < 0 || seedX >= width || seedY >= height) {
    throw new RangeError(`种子 (${seedX}, ${seedY}) 超出文档范围。`);
  }
  const tolerance = Math.min(255, Math.max(0, options.tolerance)) / 255;

  const flood = options.flood ?? defaultFlood;
  const raw = await flood(
    pixels,
    width,
    height,
    Math.floor(seedX),
    Math.floor(seedY),
    tolerance,
    options.contiguous,
  );
  throwIfAborted(options.signal);

  // 稀疏化：整行 memcpy 切块（逐像素分支的写法在 4096² 上要多花近 100ms，
  // 见 M2-08 的性能预算），抽成了 SelectionMask.fromDenseGrayscale 供
  // 选择主体（select-subject.ts）复用同一段逻辑
  return SelectionMask.fromDenseGrayscale(width, height, raw);
}
